import { useState, useRef, useEffect, useMemo } from "react";

import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { getEffectiveAvailability } from "../utils/availability";
import { apiRequest } from "../utils/api";
import { callWithWalletGate, WALLET_GATE_CANCELLED } from "../utils/walletGate";
import { detectCarCategory, formatCarName, resolveShortName } from "../utils/carCategory";
import { Icon } from "../components/Icon";
import LeadHelpButton from "../components/LeadHelpButton";
import { LaptopTopBar, DesktopFooter } from "../components/home/DesktopAtoms";
import { useMyRecentLeads, tripWhen, shortPlace } from "../components/home/HomeAtoms";
import useLeadRating, { RATING_PARTS } from "../hooks/useLeadRating";
import { CARD, titleCase, firstNameOf, areaOf, StarGlyph, RatingsSummary, RateCard, TrustLine } from "../components/detail/DetailAtoms";
import { calculateFare } from "../shared/pricing/pricingEngine";
import { calculateTripMetrics } from "../utils/distanceUtils";
import { track } from "../utils/analytics";
import { CITY_LIST_AP } from "../utils/cities";
import { readTripExtras } from "../utils/tripExtras";
import { fillText, dateLocale, clockText } from "../utils/leadDisplay";
import { useLocalNames } from "../utils/localNames";
import { CAR_TYPES, carTypeMatches, nativeLanguageNames, fuelLabel, featureBadgeKey } from "../utils/listChoices";
import { askOnBehalf, onBehalfBody, ON_BEHALF_CANCELLED } from "../components/OnBehalfHost";
import { WantUsToBook } from "../components/home/CallToBook";
import { startAppCall } from "../utils/appCall";
import InstantBookingSheet, { seatClassOf } from "../components/InstantBookingSheet";
import { showBusinessLimit } from "../components/BusinessLimitHost";
import { carWords } from "../data/seoPages";
import { useSeoIndex } from "../components/seo/SeoAtoms";
import { usePageMeta } from "../hooks/usePageMeta";

/* The customer's page for one car: the photos, what the car is, the trip and its price,
   who drives it, and one way to act - Call, at the bottom of the screen at all times.
   The same look as the home lists (components/home/HomeAtoms): white cards on a
   hairline border, Sramika green for the one thing to do, every word in the reader's
   language. Sizes are written at full design size; on a phone every button renders at
   four-fifths of it (index.css), and on a laptop .hm halves the pixel sizes with the
   rest of the page. */

/* The price lines, in the reader's language. The pricing engine writes them in English
   ("Driver Allowance (2 Days | 36hrs)") because the server shares it; each known shape
   is read back into its parts and said again with the dictionary's words. A line that
   matches nothing is shown as the engine wrote it. */
function fareLineText(label, t) {
  const s = String(label || "").trim();
  const count = (n, one, many) => (Number(n) === 1 ? t(one) : fillText(t(many), { n }));
  let m;
  if (/^base fare$/i.test(s)) return t("cv_lBase");
  if (/^drop fare$/i.test(s)) return t("cv_lDrop");
  if (/^fuel charge$/i.test(s)) return t("cv_lFuel");
  if ((m = /^driver allowance \((\d+) days? \| (\d+)\s*hrs?\)$/i.exec(s))) {
    return fillText(t("cv_lDriver"), { x: `${count(m[1], "cv_oneDay", "cv_nDays")} · ${count(m[2], "cv_oneHr", "cv_nHrs")}` });
  }
  if ((m = /^night allowance \((\d+) nights?\)$/i.exec(s))) return fillText(t("cv_lNight"), { x: count(m[1], "cv_oneNight", "cv_nNights") });
  if ((m = /^one-way drop \(up to (\d+) km\)$/i.exec(s))) return fillText(t("cv_lDropUpTo"), { km: m[1] });
  if ((m = /^one-way drop \(first (\d+) km\)$/i.exec(s))) return fillText(t("cv_lDropFirst"), { km: m[1] });
  if ((m = /^extra (\d+) km × ₹\s*([\d.,]+)$/i.exec(s))) return fillText(t("cv_lExtraKmRate"), { km: m[1], rate: m[2] });
  if ((m = /^(\d+) hr package$/i.exec(s))) return fillText(t("cv_lPackage"), { n: m[1] });
  if ((m = /^extra (\d+) hr$/i.exec(s))) return Number(m[1]) === 1 ? t("cv_lExtraOneHr") : fillText(t("cv_lExtraHr"), { n: m[1] });
  if ((m = /^extra (\d+) km$/i.exec(s))) return fillText(t("cv_lExtraKm"), { n: m[1] });
  if ((m = /^day rent \((.+)\)$/i.exec(s))) {
    const x = m[1].split("+").map((p) => {
      const d = /(\d+) day/i.exec(p); const h = /(\d+) half-day/i.exec(p);
      return h ? count(h[1], "cv_oneHalfDay", "cv_nHalfDays") : d ? count(d[1], "cv_oneDay", "cv_nDays") : p.trim();
    }).join(" + ");
    return fillText(t("cv_lDayRent"), { x });
  }
  return s;
}

// "1 day 12 hrs", "6 hrs" - a trip's length in the reader's words.
function durationText(hours, t) {
  const hrs = Math.max(0, Number(hours) || 0);
  if (!hrs) return "";
  const d = Math.floor(hrs / 24);
  const h = Math.round(hrs % 24);
  const hText = h === 1 ? t("cv_oneHr") : fillText(t("cv_nHrs"), { n: h });
  if (!d) return hText;
  const dText = d === 1 ? t("cv_oneDay") : fillText(t("cv_nDays"), { n: d });
  return h > 0 ? `${dText} ${hText}` : dText;
}

// Monday first, as the owners' weekly hours are kept.
const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ── Cab status helpers ────────────────────────────────────────────────────────
const AWAITING_STATUSES = ["REQUESTED", "DRIVER_ACCEPTED", "ADVANCE_PENDING"];
const ON_TRIP_STATUSES  = ["CONFIRMED", "IN_PROGRESS"];

function getCabStatus(car, currentUserPhone) {
  // Check owner-set availability status first (MAINTENANCE, ON_TRIP set from dashboard)
  // Use getEffectiveAvailability to respect 24h auto-reset rule
  const ownerAvail = getEffectiveAvailability(car);
  if (ownerAvail === "MAINTENANCE") return { status: "MAINTENANCE", booking: null, isOwn: false };

  const b = car?.activeBooking;
  if (!b) {
    // If owner marked ON_TRIP but no booking, still reflect it
    if (ownerAvail === "ON_TRIP") return { status: "ON_TRIP", booking: null, isOwn: false };
    return { status: "AVAILABLE", booking: null, isOwn: false };
  }
  const isOwn = !!currentUserPhone && b.customerId === currentUserPhone;
  if (AWAITING_STATUSES.includes(b.status)) return { status: "AWAITING", booking: b, isOwn };
  if (ON_TRIP_STATUSES.includes(b.status))  return { status: "ON_TRIP",  booking: b, isOwn };
  return { status: "AVAILABLE", booking: null, isOwn: false };
}

// When a car on a trip is expected back - "Tomorrow, 8:00 PM" - in the reader's language.
function formatExpectedBack(booking, t, lang) {
  if (!booking?.tripDate) return null;
  try {
    const durationHrs = Number(booking.estimatedDurationHrs) || (booking.distanceKm ? booking.distanceKm / 50 : 4);
    const backMs = new Date(booking.tripDate).getTime() + (durationHrs + 2) * 3600000;
    const d = new Date(backMs);
    if (isNaN(d)) return null;
    return tripWhen(d.toISOString(), t, lang) || null;
  } catch { return null; }
}

const toDialable = (p) => {
  const digits = String(p || "").replace(/\D/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `+91${digits.slice(1)}`;
  return digits ? `+${digits}` : "";
};

// ── Main Component ─────────────────────────────────────────────────────────
/* `carId` comes from pages/CabPage - the car's own address for Google, /cab/<city>/<car>
   (mock Seo_8); otherwise the id is read from /car/CAR#<id>. */
export default function CarDetail({ carId: carIdProp = null } = {}) {
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const { id: routeCarId } = useParams(); // path param - only "CAR" (browser treats # as fragment)
  // Reconstruct the full carId: /car/CAR#<uuid> → pathname gives "CAR", hash gives "#<uuid>"
  // location.hash includes the leading "#", so routeCarId + location.hash = "CAR#<uuid>" exactly
  const fullCarId = carIdProp || (routeCarId ? (routeCarId + location.hash) : null);
  // Example: routeCarId="CAR", location.hash="#e490d883-09c0-4926-b44b-94f21093e88b"
  //   → fullCarId = "CAR#e490d883-09c0-4926-b44b-94f21093e88b"
  //   → encodeURIComponent(fullCarId) = "CAR%23e490d883-09c0-4926-b44b-94f21093e88b"

  const { user } = useAuth();
  const { t, lang } = useLanguage();
  // Names, the car and places in the reader's script (utils/localNames) - display only.
  const ln = useLocalNames();
  /* The lead model is the product. There is no flag here any more: the booking
     model it used to select against has been removed, and while it survived it read
     false in production — so the retired model was what customers were shown by
     default. Nothing about pricing or contact should depend on config again. */
  
  // ── State ──
  const [imgIdx, setImgIdx] = useState(0);
  const [ratingData, setRatingData] = useState({ avgRating: null, ratingCount: 0, ratings: [] });
  const [fullScreenData, setFullScreenData] = useState(null);
  // The owner's seven days, opened from the one-line summary.
  const [showWeek, setShowWeek] = useState(false);
  // "Link copied", where a phone has no share sheet.
  const [shareNote, setShareNote] = useState("");
  // The small top bar (Back, name, Share) once the photo has scrolled away.
  const [compactBar, setCompactBar] = useState(false);
  const heroRef = useRef(null);
  const [notified, setNotified] = useState(false);
  const [showFareBreakdown, setShowFareBreakdown] = useState(false);
  const [showPricingCalc, setShowPricingCalc] = useState(false);
  const [loadingCall, setLoadingCall] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  // Live driving time from the car's driver address to the pickup (Distance Matrix via
  // /distance). null = missing coords / failed → the ETA row is hidden.
  const [carEtaMins, setCarEtaMins] = useState(null);
  const [calcHours, setCalcHours] = useState("");
  const [calcKm, setCalcKm] = useState("");
  const [calcResult, setCalcResult] = useState(null);

  // ── Public-link fetch (shared / direct / bookmarked / refreshed links) ──
  const [fetchedCar, setFetchedCar] = useState(null);
  const [carFetchLoading, setCarFetchLoading] = useState(!state?.car && !!fullCarId);
  const [carFetchError, setCarFetchError] = useState(false);
  const [searchParams] = useSearchParams();
  const isColdLoad = !state?.car && !!fullCarId;

  useEffect(() => {
    // Fast path: state.car present (normal list navigation) — skip fetch entirely
    if (state?.car) return;
    if (!fullCarId) { setCarFetchError(true); setCarFetchLoading(false); return; }

    let cancelled = false;
    setCarFetchLoading(true);
    setCarFetchError(false);

    // Public endpoint returns a flat whitelist shape. Normalize it into the same
    // shape the component render already reads so zero downstream changes are needed:
    //   model  ← carDetails.model   seats ← carDetails.seats
    //   fuelType ← carDetails.fuelType   makeYear ← carDetails.year
    //   ownerDriver.name ← driver.firstName   photos ← photos[]
    // fullCarId = "CAR#<uuid>" → encodeURIComponent → "CAR%23<uuid>" for the API path
    apiRequest(`/public/cars/${encodeURIComponent(fullCarId)}`)
      .then(data => {
        if (cancelled) return;
        const normalized = {
          carId:        data.carId,
          status:       data.status,
          model:        data.model,
          carDetails: {
            model:              data.model,
            seats:              data.seats,
            fuel:               data.fuel,
            fuelType:           data.fuel,
            transmission:       data.transmission,
            manufacturingYear:  data.year,
            year:               data.year,
            vehicleType:        data.vehicleType
          },
          makeYear:        data.year,
          photos:          data.photos || [],
          uploads: {
            carImages: Array.isArray(data.photos) ? data.photos.map(p => typeof p === 'string' ? p : (p?.url || p?.location || p?.src || p?.S || p)) : []
          },
          ownerDriver: {
            name: data.driver?.firstName || "",
            age:  data.driver?.age
          },
          driverPhoto:     data.driver?.photo ? data.driver.photo.replace(/#/g, "%23") : null,
          averageRating:   data.rating?.avgRating  ?? null,
          ratingCount:     data.rating?.ratingCount ?? 0
        };
        setFetchedCar(normalized);
        setCarFetchLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        // 404 or network error — show "not available" screen
        setCarFetchError(true);
        setCarFetchLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullCarId]);

  const touchStartX = useRef(0);

  /* Google (mock Seo_8): this car's own address as the canonical one, and its title, from
     the build's list of car pages - so /car/CAR#<id> and /cab/... are one page to Google. */
  const seoIndex = useSeoIndex();
  const seoId = fullCarId || state?.car?.carId || null;
  const seoPath = useMemo(() => Object.entries(seoIndex?.cars || {}).find(([, id]) => id === seoId)?.[0] || null, [seoIndex, seoId]);
  useEffect(() => {
    if (!seoPath) return undefined;
    const tag = document.querySelector('link[rel="canonical"]');
    const before = tag?.getAttribute("href");
    tag?.setAttribute("href", `https://sramika.com${seoPath}`);
    return () => { if (tag && before) tag.setAttribute("href", before); };
  }, [seoPath]);

  // ── Data Extraction ──
  // Fast path: use state.car directly. Fallback: use fetchedCar from public endpoint.
  const car = state?.car || fetchedCar || {};
  // The tab and Google show the car (mock Seo_8), once its city is known.
  usePageMeta(car.city ? carWords({ car }).title : null, car.city ? carWords({ car }).description : null);
  const tripType = state?.tripType || (isColdLoad ? searchParams.get("tripType") : null) || "ROUND";
  const fromLocation = state?.fromLocation || (isColdLoad ? searchParams.get("pickup") || searchParams.get("from") : null);
  const toLocation = state?.toLocation || (isColdLoad ? searchParams.get("drop") || searchParams.get("to") : null);

  // ── Dynamic Route States ──
  const [distanceKm, setDistanceKm] = useState(state?.distanceKm || 0);
  const [driveDurationMins, setDriveDurationMins] = useState(0);
  const urlHours = searchParams.get("hours");
  const initHours = isColdLoad ? (urlHours !== null ? Number(urlHours) : 4) : 0;
  const [hours, setHours] = useState(state?.hours !== undefined ? state.hours : initHours);
  const [cityPricing, setCityPricing] = useState(state?.cityPricing || null);

  // ── Public Fare Fetch ──
  const [publicFareData, setPublicFareData] = useState(null);
  const [publicFareLoading, setPublicFareLoading] = useState(false);
  const [publicFareError, setPublicFareError] = useState(false);

  // ── Persist Cold-Load Route to Home ──
  useEffect(() => {
    if (isColdLoad && fromLocation) {
      try {
        const fromStr = typeof fromLocation === "string" ? fromLocation : fromLocation.value || fromLocation.description || fromLocation.name || "";
        sessionStorage.setItem("sramika_ss_home_fromLocation", JSON.stringify({ value: fromStr, selected: true }));
        
        if (toLocation) {
          const toStr = typeof toLocation === "string" ? toLocation : toLocation.value || toLocation.description || toLocation.name || "";
          sessionStorage.setItem("sramika_ss_home_toLocation", JSON.stringify({ value: toStr, selected: true }));
        }
        
        if (tripType) {
          sessionStorage.setItem("sramika_ss_home_tripType", JSON.stringify(tripType));
        }

        const fDate = searchParams.get("fromDate");
        if (fDate) sessionStorage.setItem("sramika_ss_home_fromDateTime", JSON.stringify(fDate));
        
        const tDate = searchParams.get("toDate");
        if (tDate) sessionStorage.setItem("sramika_ss_home_toDateTime", JSON.stringify(tDate));

        // Derive city from 'fromLocation'
        const parts = fromStr.split(',').map(s => s.trim());
        let city = null;
        for (const p of parts) {
          const match = CITY_LIST_AP.find(c => c.toLowerCase() === p.toLowerCase());
          if (match) { city = match; break; }
        }
        if (!city) {
          city = parts.length > 1 ? parts[1] : parts[0];
        }
        if (city) localStorage.setItem("sramika_selected_city", city);
      } catch (e) {
        // ignore
      }
    }
  }, [isColdLoad, fromLocation, toLocation, tripType, searchParams]);

  useEffect(() => {
    if (!isColdLoad || !fullCarId || !fromLocation || !toLocation) return;
    let cancelled = false;
    setPublicFareLoading(true);
    setPublicFareError(false);
    
    const fromStr = typeof fromLocation === "string" ? fromLocation : fromLocation.value || fromLocation.description || fromLocation.name;
    const toStr = typeof toLocation === "string" ? toLocation : toLocation.value || toLocation.description || toLocation.name;
    const engineTripType = ["ROUND_TRIP", "OUTSTATION"].includes(tripType) ? "ROUND" : (tripType === "DROPPING" ? "ONE_WAY" : tripType);

    apiRequest(`/public/cars/${encodeURIComponent(fullCarId)}/price`, {
      method: "POST",
      body: JSON.stringify({
        pickup: fromStr,
        drop: toStr,
        tripType: engineTripType,
        hours,
        // The pickup time, when the link carries one: the night halt follows it.
        startAt: searchParams.get("fromDate") || undefined
      })
    }).then(res => {
      if (cancelled) return;
      setPublicFareData(res);
      setDistanceKm(res.distanceKm || 0);
      setPublicFareLoading(false);
    }).catch(err => {
      if (cancelled) return;
      setPublicFareError(true);
      setPublicFareLoading(false);
    });
    
    return () => { cancelled = true; };
  }, [fullCarId, isColdLoad, fromLocation, toLocation, tripType, hours, searchParams]);

  // Fetch city pricing dynamically if missing
  useEffect(() => {
    if (isColdLoad) return;
    const city = state?.selectedCity || car.city || "";
    if (!city) return;
    apiRequest(`/pricing?city=${encodeURIComponent(city)}`)
      .then(res => setCityPricing(res?.data?.pricing || res?.pricing || null))
      .catch(() => {});
  }, [state?.selectedCity, car.city]);

  // Calculate trip distance dynamically using Google Maps API
  useEffect(() => {
    if (isColdLoad) return;
    let active = true;
    const val = (loc) => loc?.value || loc || "";
    const from = val(fromLocation);
    const to = val(toLocation);
    const legs = [from, ...(state?.stops || []).map(val).filter(Boolean), to].filter(Boolean);

    async function calc() {
      if (legs.length < 2) { 
        if (active) { setDistanceKm(0); setDriveDurationMins(0); }
        return; 
      }
      let totDist = 0;
      let totTime = 0;
      for (let i = 0; i < legs.length - 1; i++) {
        const res = await calculateTripMetrics(legs[i], legs[i + 1]);
        totDist += res.distanceKm || 0;
        totTime += res.durationMinutes || 0;
      }
      if (active) {
        const finalDist = (tripType === "ROUND" || tripType === "ROUND_TRIP") ? totDist * 2 : totDist;
        setDistanceKm(finalDist);
        setDriveDurationMins(totTime);
      }
    }
    calc().catch(() => { 
      if (active) { setDistanceKm(0); setDriveDurationMins(0); }
    });
    return () => { active = false; };
  }, [fromLocation, toLocation, state?.stops, tripType]);

  // Calculate trip hours dynamically
  // Cold-load: skip recompute — no state?.fromDateTime/returnDateTime exists, so
  // the ROUND branch always yields baseHours=0 and would overwrite the URL hours
  // param (e.g. hours=48) that the sharer wrote. Preserve URL value instead.
  useEffect(() => {
    if (isColdLoad) return;
    let baseHours = 0;
    if (tripType === "ONE_WAY") {
      baseHours = driveDurationMins / 60;
    } else {
      const fromDT = state?.fromDateTime || "";
      const returnDT = state?.returnDateTime || "";
      if (fromDT && returnDT) {
        const diffMs = new Date(returnDT) - new Date(fromDT);
        baseHours = diffMs > 0 ? diffMs / (1000 * 60 * 60) : 0;
        if (driveDurationMins > 0) {
          baseHours += driveDurationMins / 60;
        }
      } else if (state?.hours > 0) {
        // Fallback: Preserve the valid hours computed by Home.jsx (which safely
        // uses toDateTime as a fallback) rather than overwriting it with 0.
        baseHours = state.hours;
      } else if (driveDurationMins > 0) {
        baseHours += driveDurationMins / 60;
      }
    }
    setHours(baseHours);
  }, [isColdLoad, state?.fromDateTime, state?.returnDateTime, tripType, driveDurationMins]);

  const rawModel = car.carDetails?.model || car.carModel || "Car";
  const model = formatCarName(rawModel);
  const version = car.carDetails?.version || car.version || "";
  const fullCarName = `${rawModel}${version && version !== "Standard" ? " - " + version : ""}`;
  const seats = car.carDetails?.seats || car.seatCount || "";
  const category = detectCarCategory(rawModel, seats);

  /* Only what the owner gave. These used to default to Diesel, Manual and 2022 when
     missing, which printed a made-up fuel, gearbox and year on a real car's page. */
  const fuel = car.fuelType || car.carDetails?.fuelType || car.carDetails?.fuel || "";
  const trans = car.transmission || car.carDetails?.transmission || "";
  const year = car.makeYear || car.carDetails?.manufacturingYear || car.carDetails?.year || car.car1?.manufacturingYear || "";

  const driverName = car.ownerDriver?.name || car.driverName || car.ownerName || "";
  const rawDriverPhoto = car.uploads?.driverPhoto || car.driverPhoto || car.driverImageUrl || null;
  const driverPhoto = rawDriverPhoto ? rawDriverPhoto.replace(/#/g, "%23") : null;
  const isApproved = String(car.status || car.approvalStatus || "").toUpperCase() === "APPROVED";

  const rawCarImages = (car.photos && car.photos.length > 0) ? car.photos : (car.carImageUrl ? [car.carImageUrl] : (car.uploads?.carImages || []));
  const carImages = rawCarImages.map(img => typeof img === 'string' ? img.replace(/#/g, "%23") : img);

  // ── Ratings Fetch ──
  const refreshRatings = () => {
    if (car.carId) {
      apiRequest(`/ratings/${encodeURIComponent(car.carId)}`)
        .then(res => { if (res && res.ratings) setRatingData(res); })
        .catch(() => {});
    }
  };
  useEffect(() => { 
    refreshRatings(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.carId]);

  const hasRoute = !!((fromLocation?.value || fromLocation) && (toLocation?.value || toLocation)) && !publicFareError;

  const fare = (() => {
    if (!hasRoute) return null;
    if (state?.fare) return state.fare;
    if (isColdLoad && publicFareData) {
      return {
        original: publicFareData.originalFare || publicFareData.fare,
        final: publicFareData.fare,
        discount: (publicFareData.originalFare && publicFareData.originalFare > publicFareData.fare) ? (publicFareData.originalFare - publicFareData.fare) : 0,
        breakdown: {
          totalKmBothWays: publicFareData.billedDistanceKm || publicFareData.distanceKm,
          tripHours: publicFareData.durationHrs,
          cabPartnerDiscountAmount: (publicFareData.originalFare && publicFareData.originalFare > publicFareData.fare) ? (publicFareData.originalFare - publicFareData.fare) : 0
        }
      };
    }
    if (!isColdLoad && calculateFare && distanceKm > 0) {
      return calculateFare(car, hours, distanceKm, tripType, cityPricing, { startAt: state?.fromDateTime || state?.pickupDateTime });
    }
    return null;
  })();

  const displayFare = fare ? (() => {
    const bd = fare.breakdown || {};
    /* The ₹1/km "Sramika Discount" is gone. It inflated the base fare and then showed
       the inflation back as a saving, which is not a discount — the real difference
       against the typical fare is, and that is what the breakdown shows now. */
    const typeBDiscount   = bd.cabPartnerDiscountAmount || bd.partnerOffer || 0;
    const listedPrice     = fare.original || 0;
    
    let finalAmount, customerFeeAmount, totalTripCost;
    
    /* One model, so one calculation. The customer pays the partner's fare and
       nothing else — there is no service fee in the lead model, and the branch that
       used to add 4% here has gone with the booking model it belonged to. It was
       doing real damage: the flag read false in production, so this page added 4%
       to the headline price while the breakdown sheet below showed the fare without
       it. Two numbers for one trip, neither of them labelled as including a fee.
       customerFeeAmount stays at zero rather than being deleted, because the lead
       payload still reads it. */
    const driverDiscount = typeBDiscount;
    const driverFare = Math.max(0, listedPrice - driverDiscount);
    const promoDiscount = 0;
    const youPay = Math.max(0, driverFare - promoDiscount);

    finalAmount = youPay; // Backward compatibility for finalPrice payload
    customerFeeAmount = 0;
    totalTripCost = youPay;

    return {
      finalAmount, totalTripCost, listedPrice, typeBDiscount, customerFeeAmount,
      driverFare, driverDiscount, youPay, promoDiscount
    };
  })() : null;

  // A trip starting within 6 hours: an immediate booking, not one driver's call (mock AppCall_3).
  const [instantOpen, setInstantOpen] = useState(false);
  const handleCallDriver = async () => {
    track("call_tapped", { type: "car", method: "page", carId: car?.carId });
    // callAfterLogin: back from the login, the call the customer asked for starts by itself.
    const navState = { car, tripType, distanceKm, hours, fromLocation, toLocation, cityPricing, stops: state?.stops, callAfterLogin: true };
    const callPayload = () => {
      const val = (loc) => loc?.value || loc?.name || loc || "Pickup";
      return {
        ownerId: car?.ownerPhone || car?.ownerId || car?.driverPhone || car?.id || "UNKNOWN",
        carId: car?.id || car?.carId || "UNKNOWN",
        customerName: user?.name || "Customer",
        customerPhone: user?.phone,
        pickupLocation: typeof fromLocation === "string" ? fromLocation : val(fromLocation),
        dropLocation: typeof toLocation === "string" ? toLocation : val(toLocation),
        distanceKm: displayFare?.bd?.totalKmBothWays || distanceKm || 10,
        tripType,
        pickupDateTime: pickupDt,
        dropDateTime: dropDt,
        durationHours: hours,
        referenceFare: displayFare?.listedPrice || 0,
        driverFare: displayFare?.finalAmount || 0,
        driverDiscount: Math.max(0, (displayFare?.listedPrice || 0) - (displayFare?.finalAmount || 0)),
        finalPrice: displayFare?.finalAmount || 0,
        category: car?.category || category?.key || "Hatchback",
        // Passengers, luggage and the note from the trip form, for the partner to see.
        ...readTripExtras()
      };
    };
    // App calls (mock AppCall_2): no login - Sramika's number and a code; the owner's number is never shown.
    setLoadingCall(true);
    const dialing = await startAppCall({ kind: "car", page: "car", body: callPayload() });
    setLoadingCall(false);
    if (dialing === true) return;
    if (!user) {
      // Derive fragment from car.carId ("CAR#<uuid>" → "#<uuid>"), not location.hash
      // location.hash is "" when the sharer arrived via list nav — car.carId is always reliable
      const carIdParts = (car?.carId || "").split("#");
      const carHash = carIdParts.length > 1 ? "#" + carIdParts.slice(1).join("#") : (location.hash || "");
      const carPathSegment = carIdParts[0] || routeCarId;
      let returnPath = "/car/" + carPathSegment;
      const params = new URLSearchParams();
      const val = (loc) => typeof loc === "string" ? loc : loc?.value || loc?.description || loc?.name;
      if (fromLocation) params.set("from", val(fromLocation));
      if (toLocation) params.set("to", val(toLocation));
      const engineTripType = ["ROUND_TRIP", "OUTSTATION"].includes(tripType) ? "ROUND" : (tripType === "DROPPING" ? "ONE_WAY" : tripType);
      if (tripType) params.set("tripType", engineTripType);
      if (hours !== undefined && hours !== null) params.set("hours", hours);
      // Preserve dates through login redirect so times survive auth
      if (pickupDt) params.set("fromDate", pickupDt);
      if (dropDt)   params.set("toDate",   dropDt);
      const searchStr = params.toString();
      if (searchStr) returnPath += "?" + searchStr;
      returnPath += carHash;
      navigate("/login", { state: { redirectTo: returnPath, payload: navState } });
      return;
    }
    setLoadingCall(true);
    try {
      const payload = callPayload();
        // Desk staff: for themselves, or for a customer (mock CallBook_1).
        const ob = await askOnBehalf();
        if (ob === ON_BEHALF_CANCELLED) return;
        // A call Sramika connects may cost money: the gate asks first, then calls again.
        const res = await callWithWalletGate(({ confirmPaid }) =>
          apiRequest("/leads/call-driver", { method: "POST", body: JSON.stringify({ ...payload, ...onBehalfBody(ob), confirmPaid }) }));
        if (res?.success) track("call_placed", { type: "car", method: "page", carId: car?.carId, leadId: res?.leadId });
        if (res?.code === "DIRECT_CALL" && res?.success) {
          setCallStatus({ leadId: res.leadId, ownerId: payload.ownerId, status: "direct", partnerPhone: res.partnerPhone, message: res.message });
          /* Dial on this tap, not the next one.
             This page used to reveal the number and turn the button into "Call
             98xxxxxxxx", so reaching a partner took two presses and the second one
             looked like a different action from the first. The list has always done
             it in one, and a customer who taps Call on the card and Call on the
             detail page should not meet two different products.
             The lead is recorded as called BEFORE handing over to the dialler:
             navigating away can cancel an in-flight request, keepalive is what
             carries it, and the partner's "did this become a trip?" question is only
             asked of leads that were actually rung. */
          if (res?.partnerPhone) {
            if (res?.leadId) {
              apiRequest(`/leads/${encodeURIComponent(res.leadId)}/call-tapped`, {
                method: "POST",
                keepalive: true,
                body: JSON.stringify({ ownerId: payload.ownerId }),
              }).catch(() => {});
            }
            window.location.href = `tel:${toDialable(res.partnerPhone)}`;
          }
        } else if (res?.success) {
          setCallStatus({ leadId: res.leadId, ownerId: payload.ownerId, status: "contacting", message: res.message });
        } else {
          alert(t("callInitiationError") || res?.error || "Could not connect call.");
        }
    } catch (err) {
      if (showBusinessLimit(err)) return; // one business call every 6 hours (mock B2BLimit_1)
      if (err?.code === WALLET_GATE_CANCELLED) {
        // The customer chose not to pay for this call - nothing to report.
      } else if (err?.code === "SUPER_ADMIN_BLOCKED" || err?.status === 403) {
        alert("Super admin accounts cannot place customer calls. Use a customer account to test this flow.");
      } else if (err?.code === "IVR_UNAVAILABLE" || err?.status === 503) {
        alert(t("dp_callFail"));
      } else {
        alert(t("callInitiationError") || "Error initiating call.");
      }
    } finally {
      setLoadingCall(false);
    }
  };

  useEffect(() => {
    let intervalId;
    let attempts = 0;
    if (callStatus?.leadId && callStatus?.status === "contacting") {
      intervalId = setInterval(async () => {
        attempts++;
        if (attempts > 20) {
          clearInterval(intervalId);
          setCallStatus(prev => ({ ...prev, status: "timeout", message: "Call status check timed out." }));
          return;
        }
        try {
          const res = await apiRequest(`/leads/${callStatus.leadId}/call-status?ownerId=${encodeURIComponent(callStatus.ownerId)}`);
          if (res?.success) {
             setCallStatus(prev => ({ ...prev, status: res.status, message: res.message }));
             if (res.status !== "contacting") {
               clearInterval(intervalId);
             }
          }
        } catch (e) {
          console.error("Poll call status error", e);
        }
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [callStatus?.leadId, callStatus?.status, callStatus?.ownerId]);

  // ── Track Time on Page ──
  useEffect(() => {
    const start = Date.now();
    return () => {
      const timeSpentSeconds = Math.round((Date.now() - start) / 1000);
      track('car_detail_viewed', {
        carId: car?.carId,
        category: category?.key,
        fare: fare?.final,
        timeSpentSeconds
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live ETA from the car owner's address to the customer's pickup, via Distance Matrix.
  // The car record keeps address + coordinate keys at the ROOT (car.address / addressLat /
  // addressLng); the coords are null today, but the address string is real. The enriched
  // Home→CarDetail object may instead carry these under ownerDriver, so we read root first
  // and fall back to ownerDriver. Origin is coords when present, otherwise the address
  // TEXT (geocoded by Distance Matrix into an accurate time) — deliberately NOT the
  // city-centre eta rings. Destination is the pickup coords, or its text. Hidden when
  // there is no origin, no pickup, or the call fails.
  useEffect(() => {
    const dLat = car?.addressLat ?? car?.ownerDriver?.addressLat;
    const dLng = car?.addressLng ?? car?.ownerDriver?.addressLng;
    const addr = car?.address || car?.ownerDriver?.address || "";
    const origin = (dLat != null && dLng != null) ? `${dLat},${dLng}` : addr;
    const pLat = fromLocation?.location?.lat, pLng = fromLocation?.location?.lng;
    const dest = (pLat != null && pLng != null)
      ? `${pLat},${pLng}`
      : (typeof fromLocation === "string" ? fromLocation : (fromLocation?.value || ""));
    if (!origin || !dest) { setCarEtaMins(null); return; }
    let active = true;
    calculateTripMetrics(origin, dest)
      .then(res => { if (active) setCarEtaMins(res?.durationMinutes > 0 ? res.durationMinutes : null); })
      .catch(() => { if (active) setCarEtaMins(null); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.addressLat, car?.addressLng, car?.address, car?.ownerDriver?.addressLat, car?.ownerDriver?.addressLng, car?.ownerDriver?.address, fromLocation?.location?.lat, fromLocation?.location?.lng]);

  const avgRating = ratingData.avgRating ?? car.averageRating ?? null;
  const ratingCount = ratingData.ratingCount ?? car.ratingCount ?? 0;
  const hasRating = avgRating !== null && ratingCount > 0;

  const aggCriteria = (() => {
    const keys = ["punctuality", "driverBehaviour", "rideComfort", "cleanliness", "safety"];
    const defaults = Object.fromEntries(keys.map(k => [k, null]));

    if (ratingData.avgCriteria) {
      return { ...defaults, ...ratingData.avgCriteria };
    }

    const ratings = ratingData.ratings || [];
    if (ratings.length === 0) return defaults;
    const sums = Object.fromEntries(keys.map(k => [k, 0]));
    const counts = Object.fromEntries(keys.map(k => [k, 0]));
    ratings.forEach(r => {
      const c = r.criteria || {};
      keys.forEach(k => {
        if (typeof c[k] === "number" && c[k] > 0) {
          sums[k] += c[k];
          counts[k]++;
        }
      });
    });
    return Object.fromEntries(keys.map(k => [
      k, counts[k] > 0 ? Math.round((sums[k] / counts[k]) * 10) / 10 : null
    ]));
  })();

  const handleTouchStart = (e) => (touchStartX.current = e.changedTouches[0].screenX);
  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    if (carImages.length <= 1) return;
    if (Math.abs(diff) > 50) setImgIdx(p => (diff > 0 ? (p >= carImages.length - 1 ? 0 : p + 1) : (p <= 0 ? carImages.length - 1 : p - 1)));
  };

  // Cold-load: read departure/return times from URL params (fromDate / toDate).
  // Logged-in flow passes these via nav-state (pickupDateTime / dropDateTime).
  const urlFromDate  = isColdLoad ? searchParams.get("fromDate")  : null;
  const urlToDate    = isColdLoad ? searchParams.get("toDate")    : null;
  const pickupDt = state?.pickupDateTime || state?.pickupDate || urlFromDate || null;
  const dropDt   = state?.dropDateTime   || state?.dropDate   || urlToDate   || null;

  // Local pickup name (e.g. "Gollapudi") for the ETA row — the first segment of the
  // chosen pickup text.
  const pickupText = typeof fromLocation === "string" ? fromLocation : (fromLocation?.value || fromLocation?.description || fromLocation?.name || "");
  const pickupAreaLabel = pickupText ? pickupText.split(",")[0].trim() : "";

  // Open the trip picker (mirrors the "Select pickup & drop points" button) — reused by
  // the empty-state prompt and the "Change" control in the Your Trip card. The cab picker
  // seeds its form from the TOP-LEVEL nav state (init = navState), so the current trip is
  // passed at the top level — not nested under returnState — for "Change" to open the
  // picker PREFILLED. The location objects are passed whole, so their coordinates survive.
  const openTripPicker = () => {
    const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));
    const tripFields = clean({
      selectedCity: state?.selectedCity || undefined,
      fromLocation: fromLocation ? (typeof fromLocation === 'string' ? { value: fromLocation, selected: true } : fromLocation) : undefined,
      toLocation:   toLocation   ? (typeof toLocation   === 'string' ? { value: toLocation,   selected: true } : toLocation)   : undefined,
      tripType,
      fromDateTime: pickupDt || undefined,
      returnDateTime: dropDt  || undefined,
      stops: state?.stops || undefined,
    });
    const navTo = { returnToCar: car, ...tripFields };
    // Setting the trip needs no login - only Call does (19 Oct 2026).
    navigate("/location-picker", { state: navTo });
  };

  // ── Cab status for detail view ──
  const { status: cabStatus, booking: activeBooking, isOwn: isOwnBooking } = getCabStatus(car, user?.phone);
  const isUnavailable = cabStatus !== "AVAILABLE" && !isOwnBooking;
  const expectedBack = formatExpectedBack(activeBooking, t, lang);
  // Why Call is not offered right now, in the reader's language.
  const unavailableText = cabStatus === "MAINTENANCE" ? t("hm_repair")
    : cabStatus === "ON_TRIP" ? (expectedBack ? fillText(t("cv_onTripBack"), { when: expectedBack }) : t("cv_onTrip"))
    : t("cv_notFree");

  /* Call tapped before signing in (here or on a card): the login brings the customer back
     to this car and the call they asked for starts - once, and only with a price to record
     and the car free. The flag is dropped from the history entry first, so Back or a
     refresh never calls again. */
  const autoCalled = useRef(false);
  useEffect(() => {
    if (!state?.callAfterLogin || autoCalled.current || !user || !displayFare || isUnavailable) return;
    autoCalled.current = true;
    navigate(location.pathname + location.search + location.hash, { replace: true, state: { ...state, callAfterLogin: false } });
    handleCallDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.callAfterLogin, user, !!displayFare, isUnavailable]);

  const handleNotifyMe = async () => {
    if (!user) {
      navigate("/login", { state: { redirectTo: `/car/${car.carId}`, payload: state } });
      return;
    }
    /* The button says it worked - "We'll tell you" - in the reader's language. It used
       to raise an English pop-up, and raised it whether or not the request went
       through; the button changes either way, as the pop-up did. */
    setNotified(true);
    try {
      track('waitlist_joined', { carId: car.carId });
      await apiRequest("/waitlist/join", {
        method: "POST",
        body: JSON.stringify({ carId: car.carId, phone: user.phone })
      });
    } catch (err) {
      console.warn("Waitlist join failed:", err);
    }
  };

  /* Share: the link carries the trip, so whoever opens it sees the same price. The
     phone's own share sheet where there is one; on a laptop the link is copied. */
  const shareCar = async () => {
    // The fragment comes from car.carId - location.hash is "" when opened from the list.
    const idParts = (car?.carId || "").split("#");
    const carHash = idParts.length > 1 ? "#" + idParts.slice(1).join("#") : (location.hash || "");
    const url = new URL(window.location.origin + "/car/" + (idParts[0] || routeCarId));
    const val = (loc) => typeof loc === "string" ? loc : loc?.value || loc?.description || loc?.name;
    if (fromLocation) url.searchParams.set("from", val(fromLocation));
    if (toLocation) url.searchParams.set("to", val(toLocation));
    const engineTripType = ["ROUND_TRIP", "OUTSTATION"].includes(tripType) ? "ROUND" : (tripType === "DROPPING" ? "ONE_WAY" : tripType);
    if (tripType) url.searchParams.set("tripType", engineTripType);
    if (hours !== undefined && hours !== null) url.searchParams.set("hours", hours);
    // Departure and return, so the times survive to the person it is sent to.
    if (pickupDt) url.searchParams.set("fromDate", pickupDt);
    if (dropDt) url.searchParams.set("toDate", dropDt);
    url.hash = carHash;
    const link = url.toString();
    if (navigator.share) {
      try { await navigator.share({ title: fullCarName, url: link }); } catch { /* closed without sharing */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setShareNote(t("cv_linkCopied"));
      setTimeout(() => setShareNote(""), 2200);
    } catch { /* no clipboard either - nothing more to offer */ }
  };

  // The small top bar comes in once the photo, and the Back and Share on it, scroll away.
  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setCompactBar(!entry.isIntersecting), { rootMargin: "-64px 0px 0px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [carFetchLoading, carFetchError]);

  // ── The car ──
  // "Innova" - the name the list card uses - with the full model under it in grey.
  const carName = resolveShortName(rawModel) || model;
  const showFullModel = !!fullCarName && fullCarName.trim().toLowerCase() !== String(carName).trim().toLowerCase();
  const carNameL = ln(carName, "car");
  // The same one badge, in the same words, as the car's card in the list.
  const badgeKey = featureBadgeKey(car, { avgRating, categoryKey: category?.key, seats });
  const transText = /auto|amt|cvt|dct/i.test(trans) ? t("cv_auto") : /manual/i.test(trans) ? t("cv_manual") : trans;
  const typeOption = CAR_TYPES.find((o) => carTypeMatches(o.value, category));
  // "MPV" alone: the list's name for the type says "7-seater", and the seats have a chip.
  const typeText = typeOption ? (typeOption.value === "MPV" ? t("cv_mpv") : t(typeOption.key)) : "";
  const facts = [
    seats && { icon: "users", text: fillText(t("hm_nSeats"), { n: seats }) },
    year && { icon: "calendar", text: fillText(t("cv_model"), { year }) },
    fuel && { icon: "fuel", text: fuelLabel(fuel, t) },
    transText && { icon: "settings", text: transText },
    typeText && { icon: "car", text: typeText },
  ].filter(Boolean);
  const ratingsWord = Number(ratingCount) === 1 ? t("cv_oneRating") : fillText(t("cv_nRatings"), { n: ratingCount });
  const partScores = RATING_PARTS.map((k) => ({ k, v: Number(aggCriteria[k]) })).filter((p) => p.v > 0);

  // ── The driver ──
  const od = car.ownerDriver || {};
  const driverFull = ln(titleCase(driverName), "person");
  const driverFirst = ln(firstNameOf(driverName), "person");
  const driverAge = od.age || car.driverAge || "";
  // "10-15 years" → "10-15", said as "10-15 yrs driving" - it printed "10-15 years Yrs".
  const expBand = String(od.drivingExperience || od.experience || car.drivingExperience || "").trim()
    .replace(/\s*(years?|yrs?)\.?\s*$/i, "").replace(/\s+/g, " ");
  const genderRaw = String(od.gender || "").trim();
  const genderText = /^m(ale)?$/i.test(genderRaw) ? t("cv_male") : /^f(emale)?$/i.test(genderRaw) ? t("cv_female") : titleCase(genderRaw);
  const driverArea = ln(areaOf(od.address || car.address || ""), "place");
  const languagesText = nativeLanguageNames(od.languagesSpoken || car.languagesSpoken || "", lang);
  const driverFacts = [
    driverAge && fillText(t("hm_age"), { n: driverAge }),
    expBand && `${fillText(t("hm_yrs"), { yrs: expBand })} ${t("hm_driving")}`,
    genderText,
  ].filter(Boolean);

  // ── The owner's hours: one line, and the seven days behind "Week" ──
  const schedule = car?.weeklySchedule && typeof car.weeklySchedule === "object" ? car.weeklySchedule : null;
  const loc = dateLocale(lang);
  const clock = (v) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(v || ""));
    if (!m) return String(v || "");
    return clockText(new Date(2024, 0, 1, Number(m[1]), Number(m[2])), lang);
  };
  const allDay = (d) => /^0?0:00$/.test(String(d?.start || "")) && /^(23:5\d|24:00)$/.test(String(d?.end || ""));
  const hoursOf = (d) => (allDay(d) ? t("cv_24h") : (d?.start && d?.end ? `${clock(d.start)} - ${clock(d.end)}` : ""));
  const todayIdx = (() => { const js = new Date().getDay(); return js === 0 ? 6 : js - 1; })();
  // Monday 1 January 2024, so day i of the week is the 1st + i.
  const dayName = (i) => new Date(2024, 0, 1 + i).toLocaleDateString(loc, { weekday: "short" });
  const scheduleLine = (() => {
    if (!schedule) return "";
    const open = DAY_KEYS.filter((d) => schedule[d]?.enabled);
    const monHours = hoursOf(schedule.Mon);
    if (open.length === 7 && monHours && open.every((d) => hoursOf(schedule[d]) === monHours)) {
      return allDay(schedule.Mon) ? t("cv_allDay") : fillText(t("cv_everyDay"), { hours: monHours });
    }
    const today = schedule[DAY_KEYS[todayIdx]];
    const todayHours = hoursOf(today);
    const first = today?.enabled ? (todayHours ? fillText(t("cv_openToday"), { hours: todayHours }) : t("cv_dayOpen")) : t("cv_closedToday");
    return `${first} · ${fillText(t("cv_daysOpen"), { n: open.length })}`;
  })();

  // ── The trip and its price ──
  const placeOf = (p) => (typeof p === "string" ? p : p?.value || p?.description || p?.name || "");
  const fromShort = ln(shortPlace(placeOf(fromLocation)), "place");
  const toShort = ln(shortPlace(placeOf(toLocation)), "place");
  const isOneWay = tripType === "ONE_WAY" || tripType === "DROPPING";
  const isLocal = tripType === "LOCAL" || tripType === "HOURLY";
  const tripKind = isLocal ? t("cv_local") : t(isOneWay ? "lp_tabOneWay" : "lp_tabRoundTrip");
  // The same distance the list card prints beside its price - both ways on a round trip.
  const tripKm = Math.round(distanceKm || fare?.breakdown?.totalKmBothWays || 0);
  const tripBasis = [tripKind, tripKm > 0 ? fillText(t("hm_km"), { km: tripKm }) : ""].filter(Boolean).join(" · ");
  const whenFrom = tripWhen(pickupDt, t, lang);
  const whenTo = isOneWay ? "" : tripWhen(dropDt, t, lang);
  const rupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
  const priceNow = displayFare ? Math.round(displayFare.driverFare) : 0;
  const usualFare = displayFare ? Math.round(displayFare.listedPrice || 0) : 0;
  const priceReady = hasRoute && !!fare && !!displayFare && priceNow > 0;
  /* The saving, on the list card's rule: only when the partner set their own rates.
     With the boxes left blank the engine quotes a suggested share of the market, and a
     gap appears that nobody chose to offer - that is not a saving to show. A shared link
     gets the same flag from the server. */
  const partnerChoseRates = isColdLoad ? publicFareData?.partnerSetOwnRates === true : fare?.partnerSetOwnRates === true;
  const saving = priceReady && partnerChoseRates ? Math.max(0, usualFare - priceNow) : 0;
  const savingPct = saving > 0 && usualFare > 0 ? Math.round((saving / usualFare) * 100) : 0;
  // "Ramesh is 6 min from Benz Circle" - or "The partner is…" when no name was given.
  const etaText = (() => {
    if (!hasRoute || !carEtaMins || !pickupAreaLabel) return "";
    const s = fillText(t("cv_eta"), { name: driverFirst || t("cv_ownerWord"), n: carEtaMins, place: ln(pickupAreaLabel, "place") });
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  // ── Rating: only for a customer who has called this partner ──
  /* The server takes a car's rating only from a customer with an enquiry for it, so the
     card is offered to exactly them - from their own recent enquiries - and to no one
     who would be refused. It is the recent-enquiry card's rating, with the five parts. */
  const { leads: myLeads } = useMyRecentLeads(!!user);
  const myLead = useMemo(
    () => (myLeads || []).find((l) => l?.carId && l.carId === car.carId && l.sourceType !== "HIRE_DRIVER") || null,
    [myLeads, car.carId],
  );
  const rate = useLeadRating(myLead, t, { withParts: true, raterName: user?.name || undefined, onSaved: refreshRatings });

  const ON_PHOTO = "w-[44px] h-[44px] rounded-full bg-white/95 shadow-[0_2px_8px_rgba(0,0,0,0.2)] flex items-center justify-center text-[#0F172A] active:scale-95 transition-transform";
  const RING_BTN = "w-[44px] h-[44px] shrink-0 rounded-full border border-[#E5E7EB] bg-white flex items-center justify-center text-[#0F172A] active:scale-95 transition-transform";
  const CHIP = "min-h-[28px] px-2.5 py-1 rounded-full bg-[#F4F6F8] border border-[#E9EDF2] inline-flex items-center gap-1.5 text-[10.5px] font-bold text-[#334155] leading-tight";
  const GREEN_BTN = "rounded-[14px] bg-[#7BAE00] text-white font-extrabold inline-flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(123,174,0,0.3)] active:scale-[0.98] transition-transform disabled:opacity-70 md:hover:bg-[#6E9D00]";
  const nextPhoto = (step) => setImgIdx((p) => (p + step + carImages.length) % carImages.length);
  const openPriceDetails = () => setShowFareBreakdown(true);
  const callLabel = loadingCall || callStatus?.status === "contacting" ? t("connecting") : t("hm_call");
  const callAria = driverFirst ? fillText(t("hs_callName"), { name: driverFirst }) : t("hm_call");

  /* The price and Call - or, while the car is not available, the way to similar cars:
     the bar at the bottom on a phone, the right-hand panel on a laptop. */
  const actionBar = (
    isUnavailable ? (
    <>
      <p className={`mb-2 flex items-center gap-1.5 text-[11px] font-bold ${cabStatus === "MAINTENANCE" ? "text-[#B45309]" : "text-[#B91C1C]"}`}>
        <span className={`w-2 h-2 rounded-full shrink-0 ${cabStatus === "MAINTENANCE" ? "bg-[#F59E0B]" : "bg-[#DC2626]"}`} />{unavailableText}
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={handleNotifyMe} disabled={notified}
          className="flex-1 h-[52px] rounded-[14px] border border-[#CBD5E1] bg-white text-[12px] font-bold text-[#334155] inline-flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-70">
          <Icon name={notified ? "check" : "bell"} size={15} strokeWidth={2.2} />{notified ? t("cv_notified") : t("cv_notifyMe")}
        </button>
        <button type="button" onClick={() => navigate("/", { state: { searchQuery: category.label, cityFilter: car.city } })}
          className={`flex-1 h-[52px] text-[12px] ${GREEN_BTN}`}>
          <Icon name="search" size={15} stroke="#FFFFFF" strokeWidth={2.2} />{t("cv_similar")}
        </button>
      </div>
    </>
  ) : (
    /* The direct-call model, unconditionally: one tap records the enquiry and
       dials, the same flow as the car cards in the list. A repeat tap is safe -
       createMarketplaceLead holds a 24-hour idempotency key on
       owner+customer+day, so calling twice returns the same lead.
       There used to be a Book button here, the default arm of a flag that read
       false in production: a customer was shown a button that took a 10%
       advance through Razorpay against a booking the platform does not operate.
       It is kept below as a comment, for reference only. It is not a fallback,
       a mode, or a thing to re-enable behind a flag; if it ever comes back it
       comes back as a decision, not as the default that shows when a config
       fails to load.

         ═══ LEGACY BOOKING MODEL — STRICTLY NOT IN USE ═══
         <button
           onClick={() => {
             track('book_now_clicked', { carId: car?.carId, fare: fare?.final });
             const navState = { car, tripType, distanceKm, hours, fromLocation, toLocation, cityPricing };
             if (!user) {
               navigate("/login", { state: { redirectTo: "/trip-booking", payload: navState } });
             } else {
               navigate("/trip-booking", { state: navState });
             }
           }}
         …>Book…</button>
         ═══════════════════════════════════════════════════ */
    <div className="flex items-center gap-3">
      {/* The price is in the panel's own card on a laptop - here only on a phone. */}
      <div className="flex-1 min-w-0 lg:hidden">
        {priceReady ? (
          <button type="button" onClick={openPriceDetails} className="block max-w-full text-left">
            <span className="block text-[18px] font-extrabold text-[#0F172A] leading-tight tracking-tight">{rupees(priceNow)}</span>
            <span className="block text-[9.5px] font-medium text-[#64748B] truncate">{tripBasis}</span>
          </button>
        ) : !hasRoute ? (
          <button type="button" onClick={openTripPicker} className="min-h-[44px] inline-flex items-center gap-0.5 text-[12px] font-extrabold text-[#5E8700] active:opacity-70">
            {t("hm_seePrice")}<Icon name="chevronRight" size={15} strokeWidth={2.6} />
          </button>
        ) : null}
      </div>
      <button type="button" onClick={handleCallDriver} disabled={loadingCall || callStatus?.status === "contacting"} aria-label={callAria}
        className={`shrink-0 min-w-[168px] lg:flex-1 h-[52px] px-5 text-[13px] ${GREEN_BTN}`}>
        <Icon name="phone" size={17} stroke="#FFFFFF" fill="#FFFFFF" strokeWidth={1.4} />
        {callLabel}
      </button>
    </div>
  )
  );

  return (
    // Loading screen while fetching car data from shared link
    carFetchLoading ? (
      <div className="page-root hm cv bg-[#F4F6F8] flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-3">
          <div className="w-[40px] h-[40px] border-[3px] border-[#7BAE00] border-t-transparent rounded-full animate-spin" />
          <p className="text-[12px] text-[#64748B] font-medium">{t("cv_loading")}</p>
        </div>
      </div>
    ) : carFetchError ? (
      <div className="page-root hm cv bg-[#F4F6F8] flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <div className="w-[56px] h-[56px] rounded-full bg-[#FEF2F2] flex items-center justify-center">
            <Icon name="x" size={24} strokeWidth="2" className="text-[#EF4444]" />
          </div>
          <p className="text-[15px] font-extrabold text-[#0F172A]">{t("cv_goneTitle")}</p>
          <p className="text-[12px] text-[#64748B] max-w-[300px]">{t("cv_goneText")}</p>
          <button type="button" onClick={() => navigate("/")} className={`mt-2 h-[48px] px-6 text-[12px] ${GREEN_BTN}`}>{t("cv_backHome")}</button>
        </div>
      </div>
    ) : (
    <div className="page-root hm cv bg-[#F4F6F8] lg:!pb-0">
      <LaptopTopBar />

      {/* ── The small top bar, once the photo has scrolled away (a phone; a laptop has
          its top bar) ── */}
      <div className={`lg:hidden fixed top-0 inset-x-0 z-40 transition-all duration-200 ${compactBar ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"}`} aria-hidden={!compactBar}>
        <div className="bg-white/95 backdrop-blur-md border-b border-[#E5E7EB] pt-[env(safe-area-inset-top)]">
          <div className="max-w-6xl mx-auto px-3 sm:px-6 h-[60px] flex items-center gap-2.5">
            <button type="button" onClick={() => navigate("/")} aria-label={t("cv_back")} tabIndex={compactBar ? 0 : -1} className={RING_BTN}>
              <Icon name="arrowLeft" size={18} strokeWidth={2.2} />
            </button>
            <p className="flex-1 min-w-0 truncate text-[14px] font-extrabold text-[#0F172A]">{carNameL}</p>
            <button type="button" onClick={shareCar} aria-label={t("cv_share")} tabIndex={compactBar ? 0 : -1} className={RING_BTN}>
              <Icon name="share" size={17} strokeWidth={2.1} />
            </button>
          </div>
        </div>
      </div>

      {/* A laptop (mock Laptop_2): the photos and the driver on the left; the name, the
          trip, the price and Call on the right, staying in view. On a phone the wrappers
          below change nothing - one column, in this order. */}
      <div className="lg:max-w-[1240px] lg:mx-auto lg:px-6 lg:pt-6 lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-x-6 lg:items-start">

      {/* ── 1. Photos ── Swipe on a phone, arrows on a laptop; a tap opens full screen. */}
      <div className="max-w-6xl mx-auto md:px-6 md:pt-4 lg:max-w-none lg:mx-0 lg:px-0 lg:pt-0 lg:col-start-1 lg:row-start-1 lg:w-full">
        <div ref={heroRef} className="relative w-full aspect-[16/10] md:aspect-[16/9] md:rounded-2xl overflow-hidden bg-[#E2E8F0]"
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {carImages.length > 0 ? (
            <img src={carImages[imgIdx]} alt={car.city ? carWords({ car }).alt(imgIdx + 1) : fillText(t("hm_photoOf"), { n: imgIdx + 1, name: carNameL })}
              className="w-full h-full object-cover cursor-pointer" onClick={() => setFullScreenData({ images: carImages, idx: imgIdx })} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Icon name="car" size={40} className="text-[#94A3B8]" /></div>
          )}

          <button type="button" onClick={() => navigate("/")} aria-label={t("cv_back")} className={`absolute left-3 top-[calc(env(safe-area-inset-top)+12px)] ${ON_PHOTO}`}>
            <Icon name="arrowLeft" size={18} strokeWidth={2.2} />
          </button>
          <button type="button" onClick={shareCar} aria-label={t("cv_share")} className={`absolute right-3 top-[calc(env(safe-area-inset-top)+12px)] ${ON_PHOTO}`}>
            <Icon name="share" size={17} strokeWidth={2.1} />
          </button>

          {carImages.length > 1 && (
            <div className="hidden md:flex absolute inset-x-3 top-1/2 -translate-y-1/2 justify-between pointer-events-none">
              <button type="button" onClick={() => nextPhoto(-1)} aria-label={t("cv_prevPhoto")} className={`pointer-events-auto ${ON_PHOTO}`}>
                <Icon name="chevronLeft" size={18} strokeWidth={2.4} />
              </button>
              <button type="button" onClick={() => nextPhoto(1)} aria-label={t("cv_nextPhoto")} className={`pointer-events-auto ${ON_PHOTO}`}>
                <Icon name="chevronRight" size={18} strokeWidth={2.4} />
              </button>
            </div>
          )}

          {badgeKey && (
            <span className="absolute left-3 bottom-3 rounded-full bg-white px-2.5 py-1 text-[10.5px] font-extrabold text-[#5E8700] shadow-[0_1px_4px_rgba(0,0,0,0.2)] pointer-events-none">
              {t(badgeKey)}
            </span>
          )}
          {carImages.length > 1 && (
            <span className="absolute right-3 bottom-3 rounded-full bg-[rgba(15,23,42,0.66)] px-2.5 py-1 text-[10px] font-bold text-white pointer-events-none">
              {imgIdx + 1} / {carImages.length}
            </span>
          )}
          {shareNote && (
            <span role="status" className="absolute left-1/2 -translate-x-1/2 top-[calc(env(safe-area-inset-top)+18px)] rounded-full bg-[#0F172A] px-3 py-1.5 text-[10.5px] font-bold text-white shadow-lg">
              {shareNote}
            </span>
          )}
        </div>
        {/* Every photo under the big one, to click (a laptop). */}
        {carImages.length > 1 && (
          <div className="hidden lg:flex gap-2.5 mt-3">
            {carImages.slice(0, 8).map((src, i) => (
              <button key={src} type="button" onClick={() => setImgIdx(i)} aria-label={fillText(t("hm_photoOf"), { n: i + 1, name: carNameL })}
                className={`w-[108px] h-[72px] rounded-xl overflow-hidden bg-[#E2E8F0] ring-offset-2 transition ${i === imgIdx ? "ring-[3px] ring-[#7BAE00]" : "opacity-80 hover:opacity-100"}`}>
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 pt-3 pb-3 space-y-2.5 lg:contents">

        {/* The right-hand panel on a laptop: the name and facts, the trip and its price,
            and Call. */}
        <div className="space-y-2.5 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-start lg:sticky lg:top-[84px]">
        {/* ── 2. Name, rating and the facts ── */}
        <section className={`${CARD} p-3`}>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[20px] font-extrabold text-[#0F172A] leading-tight tracking-tight">{carNameL}</h1>
            {isApproved && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#F1F7E3] px-2 py-[3px] text-[10px] font-bold text-[#3F5E00]">
                <Icon name="check" size={11} strokeWidth={3} />{t("ac_verified")}
              </span>
            )}
          </div>
          {showFullModel && <p className="mt-0.5 text-[11px] font-semibold text-[#64748B] leading-snug">{ln(fullCarName, "car")}</p>}
          {/* The owner's business page (mock Seo_8, decision a) - their name, never a number. */}
          {car.ownerPage?.code && (
            <a href={`/o/${car.ownerPage.code}`} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/o/${car.ownerPage.code}`); }}
              className="mt-1 inline-flex items-center gap-1 text-[11.5px] font-bold text-[#3F5E00]">
              <Icon name="users" size={12} stroke="#5E8700" strokeWidth={2.2} />{ln(car.ownerPage.name, "person")} ›
            </a>
          )}
          {hasRating && (
            <p className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-[#334155]">
              <StarGlyph on size={14} />{Number(avgRating).toFixed(1)}
              <span className="font-medium text-[#64748B]">· {ratingsWord}</span>
            </p>
          )}
          {facts.length > 0 && (
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {facts.map((f) => (
                <li key={f.icon} className={CHIP}>
                  <Icon name={f.icon} size={13} stroke="#5E8700" strokeWidth={2} className="shrink-0" />{f.text}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ── 3. Your trip and its price ── or, before a trip, the way to one. */}
        {hasRoute ? (
          <section className={`${CARD} p-3`}>
            <div className="flex items-center gap-2">
              <h2 className="flex-1 min-w-0 text-[13px] font-extrabold text-[#0F172A]">{t("cv_yourTrip")}</h2>
              {/* Change goes to the trip form, filled in with this trip. */}
              <button type="button" onClick={openTripPicker} className="h-[36px] px-3 shrink-0 rounded-[10px] border border-[#CBD5E1] bg-white text-[11px] font-bold text-[#334155] active:scale-[0.98]">
                {t("cv_change")}
              </button>
            </div>
            <p className="mt-2 flex items-center gap-1.5 min-w-0 text-[12.5px] font-extrabold text-[#0F172A]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#7BAE00] ring-[3px] ring-[#E3F0C4] shrink-0" />
              <span className="truncate">{fromShort}</span>
              <span className="text-[#94A3B8] shrink-0">→</span>
              <Icon name="flag" size={13} stroke="#DC3545" strokeWidth={2.2} className="shrink-0" />
              <span className="truncate">{toShort}</span>
            </p>
            {(whenFrom || tripBasis) && (
              <div className="mt-1.5 space-y-0.5 text-[10px] text-[#64748B] leading-snug">
                {whenFrom && (
                  <p className="flex items-start gap-1.5"><Icon name="calendar" size={12} stroke="#94A3B8" strokeWidth={2} className="shrink-0 mt-[2px]" />
                    <span>{whenFrom}{whenTo ? ` → ${whenTo}` : ""}</span></p>
                )}
                {tripBasis && (
                  <p className="flex items-center gap-1.5"><Icon name="refresh" size={12} stroke="#94A3B8" strokeWidth={2} className="shrink-0" />{tripBasis}</p>
                )}
              </div>
            )}

            {publicFareLoading ? (
              <div className="mt-2.5 pt-2.5 border-t border-[#EFF3F7] flex justify-center">
                <div className="w-[20px] h-[20px] border-2 border-[#7BAE00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : priceReady && (
              <div className="mt-2.5 pt-2.5 border-t border-[#EFF3F7]">
                {/* The price opens its details, as "Price details" does. */}
                <div role="button" tabIndex={0} onClick={openPriceDetails} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openPriceDetails(); }}
                  className="flex items-end gap-2 cursor-pointer">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-[#64748B]">{t("cv_partnerPrice")}</p>
                    <p className="mt-1 text-[26px] font-extrabold text-[#0F172A] leading-none tracking-tight">{rupees(priceNow)}</p>
                  </div>
                  {saving > 0 && (
                    <div className="shrink-0 text-right">
                      <span className="inline-block rounded-[9px] bg-[#EEF6DC] px-2 py-1 text-[10px] font-bold text-[#4F7A00] leading-tight">
                        {fillText(t("hm_saveAmt"), { amt: saving.toLocaleString("en-IN") })}
                        {savingPct > 0 && <span className="whitespace-nowrap"> ({savingPct}%)</span>}
                      </span>
                      <p className="mt-1 text-[10px] text-[#94A3B8]">
                        {t("cv_usualFare")} <span className="line-through">{rupees(usualFare)}</span>
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <p className="min-w-0 text-[10px] text-[#64748B]">{t("cv_extrasShort")}</p>
                  <button type="button" onClick={openPriceDetails} className="min-h-[40px] -my-1.5 shrink-0 inline-flex items-center gap-0.5 text-[11px] font-extrabold text-[#5E8700] active:opacity-70">
                    {t("cv_priceDetails")}<Icon name="chevronRight" size={14} strokeWidth={2.6} />
                  </button>
                </div>
              </div>
            )}

            {/* How far the car is from the pickup - a live answer from the driver's
                address, hidden until there is one. Never estimated. */}
            {etaText && (
              <p className="mt-2.5 rounded-xl bg-[#F1F7E3] border border-[#D6E6B0] px-2.5 py-2 flex items-center gap-2 text-[10.5px] font-semibold text-[#3F5E00] leading-snug">
                <Icon name="mapPin" size={14} strokeWidth={2.2} className="shrink-0" />{etaText}
              </p>
            )}
          </section>
        ) : (
          <section className={`${CARD} p-4 flex flex-col items-center text-center`}>
            <span className="w-[40px] h-[40px] rounded-full bg-[#F1F7E3] flex items-center justify-center">
              <Icon name="mapPin" size={18} stroke="#5E8700" strokeWidth={2.2} />
            </span>
            <h2 className="mt-2 text-[13px] font-extrabold text-[#0F172A]">{t("cv_seePriceTitle")}</h2>
            <p className="mt-1 text-[10px] text-[#64748B] leading-snug max-w-[280px]">{t("cv_seePriceText")}</p>
            <button type="button" onClick={openTripPicker} className={`mt-3 h-[44px] px-5 text-[12px] ${GREEN_BTN}`}>{t("cv_setTrip")}</button>
          </section>
        )}

        {/* Call to book (mock CallBook_1): we book it for them. */}
        <WantUsToBook t={t} from="car" />
        {/* No login: the team calls back and books it (mock CallBack_1). */}
        {/* Call, in the panel (a laptop) - the phone has it in the bar at the bottom. */}
        <div className="hidden lg:block">{actionBar}</div>
        </div>

        <div className="space-y-2.5 lg:col-start-1 lg:row-start-2 lg:pt-4 lg:pb-6">
        {/* ── 4. The driver, and the owner's hours ── */}
        {(driverName || schedule) && (
          <section className={`${CARD} p-3`}>
            {driverName && (
              <>
                <div className="flex items-center gap-3">
                  <div role={driverPhoto ? "button" : undefined} tabIndex={driverPhoto ? 0 : undefined} aria-label={driverPhoto ? driverFull : undefined}
                    onClick={() => driverPhoto && setFullScreenData({ images: [driverPhoto], idx: 0 })}
                    className={`relative w-[56px] h-[56px] shrink-0 ${driverPhoto ? "cursor-pointer" : ""}`}>
                    <span className="w-full h-full rounded-[14px] overflow-hidden ring-1 ring-[#E2E8F0] bg-[#7BAE00] flex items-center justify-center">
                      {driverPhoto ? (
                        <img src={driverPhoto} alt="" className="w-full h-full object-cover object-center" />
                      ) : (
                        <span className="text-[18px] font-bold text-white">{(driverFirst || "D").slice(0, 1)}</span>
                      )}
                    </span>
                    {isApproved && (
                      <span className="absolute -top-[4px] -right-[4px] w-[18px] h-[18px] rounded-full bg-[#7BAE00] ring-2 ring-white flex items-center justify-center">
                        <Icon name="check" size={10} stroke="#FFFFFF" strokeWidth={4} />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-extrabold text-[#0F172A] leading-tight truncate">{driverFull}</p>
                    {od.dlReviewed === true && (
                      <p className="mt-0.5 flex items-center gap-1 text-[10px] font-bold text-[#5E8700]">
                        <Icon name="check" size={11} strokeWidth={3} className="shrink-0" />{t("cv_licence")}
                      </p>
                    )}
                    {driverArea && (
                      <p className="mt-0.5 flex items-center gap-1 min-w-0 text-[10px] text-[#64748B]">
                        <Icon name="mapPin" size={11} stroke="#94A3B8" strokeWidth={2} className="shrink-0" /><span className="truncate">{driverArea}</span>
                      </p>
                    )}
                  </div>
                </div>
                {driverFacts.length > 0 && (
                  <ul className="mt-2.5 flex flex-wrap gap-1.5">
                    {driverFacts.map((f) => <li key={f} className={CHIP}>{f}</li>)}
                  </ul>
                )}
                {languagesText && (
                  <p className="mt-2 flex items-start gap-1.5 text-[10.5px] font-semibold text-[#334155] leading-snug">
                    <Icon name="globe" size={13} stroke="#5E8700" strokeWidth={2} className="shrink-0 mt-[2px]" />{languagesText}
                  </p>
                )}
                {od.bio && <p className="mt-2 text-[10.5px] italic text-[#475569] leading-snug">“{od.bio}”</p>}
              </>
            )}

            {/* The hours in one line; "Week" opens the seven days. It was a seven-row
                table in English that took half the screen. */}
            {schedule && (
              <div className={`${driverName ? "mt-2.5" : ""} rounded-xl bg-[#F8FAFC] border border-[#E9EDF2]`}>
                <button type="button" onClick={() => setShowWeek((v) => !v)} aria-expanded={showWeek}
                  className="w-full min-h-[44px] px-2.5 py-1.5 flex items-center gap-2 text-left">
                  <Icon name="clock" size={14} stroke="#64748B" strokeWidth={2} className="shrink-0" />
                  <span className="flex-1 min-w-0 text-[10.5px] font-semibold text-[#334155] leading-snug">{scheduleLine}</span>
                  <span className="shrink-0 inline-flex items-center gap-0.5 text-[10.5px] font-extrabold text-[#5E8700]">
                    {t("cv_week")}<Icon name="chevronDown" size={13} strokeWidth={2.6} className={showWeek ? "rotate-180" : ""} />
                  </span>
                </button>
                {showWeek && (
                  <ul className="px-1.5 pb-1.5 space-y-0.5">
                    {DAY_KEYS.map((d, i) => {
                      const cfg = schedule[d] || {};
                      const busy = String(cfg.status || "").trim().toLowerCase() === "busy";
                      return (
                        <li key={d} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${i === todayIdx ? "bg-[#F1F7E3]" : ""}`}>
                          <span className={`w-[52px] shrink-0 text-[10.5px] ${i === todayIdx ? "font-extrabold text-[#3F5E00]" : "font-semibold text-[#0F172A]"}`}>{dayName(i)}</span>
                          <span className="flex-1 min-w-0 text-[10px] text-[#475569]">{cfg.enabled ? hoursOf(cfg) : ""}</span>
                          <span className={`shrink-0 rounded-full px-2 py-[2px] text-[9.5px] font-bold ${!cfg.enabled ? "bg-[#F1F5F9] text-[#94A3B8]" : busy ? "bg-[#FFFBEB] text-[#B45309]" : "bg-[#F1F7E3] text-[#4F7A00]"}`}>
                            {!cfg.enabled ? t("cv_dayOff") : busy ? t("cv_dayBusy") : t("cv_dayOpen")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {car.weeklyScheduleDefaulted && <p className="px-2.5 pb-2 text-[9.5px] text-[#94A3B8] leading-snug">{t("cv_notConfirmed")}</p>}
              </div>
            )}
          </section>
        )}

        {/* "Sramika Recommended" (mock Rec_1): the badge and what it stands for. */}
        {Date.parse(car.recommendedUntil || 0) > Date.now() && (
          <p className="mt-3 rounded-xl bg-[#EAF5D3] px-3 py-2 text-[12px] text-[#3F5E00] leading-snug">
            <b className="font-extrabold">✓ {t("rc_badge")}</b> · {t("rc_checked")}
          </p>
        )}

        {/* ── 5. Ratings - the stars and the five parts, in whole words ── */}
        {/* Every car shows the five parts, rated or not (mock Rate5_1 change 1). */}
        <RatingsSummary t={t} avg={hasRating ? avgRating : null} count={hasRating ? ratingCount : 0} parts={partScores} match={ratingData.matchStats || car.matchStats}
          allKeys={RATING_PARTS} partsFrom={(ratingData.ratings || []).filter((r) => Object.values(r.criteria || {}).some((v) => Number(v) > 0)).length} />

        {/* ── 5b. Rate - for a customer who has called this partner ── */}
        {myLead && (
          <RateCard t={t} rate={rate} name={driverFirst} partKeys={RATING_PARTS} />
        )}

        {/* ── 6. What is true of every car here, then Help ──
            It said "Easy Booking / Quick booking", and nothing is booked in the app. */}
        <TrustLine t={t} />
        {/* The support number and rule every other Help button uses. */}
        <LeadHelpButton label={t("cv_help")} />
        </div>
      </div>
      </div>
      <DesktopFooter t={t} />

      {/* ── Always at the bottom: the price, and Call (a phone) ── */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-[#E5E7EB] shadow-[0_-6px_18px_rgba(15,23,42,0.07)]">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 pt-2.5 pb-[max(10px,env(safe-area-inset-bottom))]">
          {actionBar}
        </div>
      </div>

      {/* ── Full-screen photos ── */}
      {fullScreenData && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center backdrop-blur-sm" onClick={() => setFullScreenData(null)}>
          <button type="button" aria-label={t("cv_close")} className="absolute top-[calc(env(safe-area-inset-top)+24px)] right-4 w-[44px] h-[44px] rounded-full bg-white/10 flex items-center justify-center text-white active:scale-95 transition-all border border-white/20 z-50" onClick={(e) => { e.stopPropagation(); setFullScreenData(null); }}>
            <Icon name="x" size={22} strokeWidth="2" />
          </button>

          {fullScreenData.images.length > 1 && (
            <div className="absolute top-[calc(env(safe-area-inset-top)+32px)] left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full text-white text-[11px] font-semibold border border-white/20 z-50">
              {fullScreenData.idx + 1} / {fullScreenData.images.length}
            </div>
          )}

          {fullScreenData.images.length > 1 && (
            <div className="absolute top-1/2 -translate-y-1/2 left-4 right-4 flex justify-between pointer-events-none z-50">
              <button type="button" aria-label={t("cv_prevPhoto")}
                onClick={(e) => {
                  e.stopPropagation();
                  setFullScreenData(p => ({ ...p, idx: p.idx <= 0 ? p.images.length - 1 : p.idx - 1 }));
                }}
                className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white pointer-events-auto border border-white/20 active:scale-95 transition-all shadow-lg hover:bg-black/60"
              >
                <Icon name="chevronLeft" size={24} strokeWidth="2" />
              </button>
              <button type="button" aria-label={t("cv_nextPhoto")}
                onClick={(e) => {
                  e.stopPropagation();
                  setFullScreenData(p => ({ ...p, idx: p.idx >= p.images.length - 1 ? 0 : p.idx + 1 }));
                }}
                className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white pointer-events-auto border border-white/20 active:scale-95 transition-all shadow-lg hover:bg-black/60"
              >
                <Icon name="chevronRight" size={24} strokeWidth="2" />
              </button>
            </div>
          )}

          <img
            src={fullScreenData.images[fullScreenData.idx]}
            alt={fillText(t("hm_photoOf"), { n: fullScreenData.idx + 1, name: carNameL })}
            className="w-full h-auto max-h-[85vh] object-contain select-none transition-transform"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => (touchStartX.current = e.changedTouches[0].screenX)}
            onTouchEnd={(e) => {
              const diff = touchStartX.current - e.changedTouches[0].screenX;
              if (fullScreenData.images.length <= 1) return;
              if (Math.abs(diff) > 50) {
                setFullScreenData(p => ({ ...p, idx: diff > 0 ? (p.idx >= p.images.length - 1 ? 0 : p.idx + 1) : (p.idx <= 0 ? p.images.length - 1 : p.idx - 1) }));
              }
            }}
          />
        </div>
      )}

      {/* ── Price details ── what the price is made of, what it leaves out, and Call. */}
      {showFareBreakdown && priceReady && (() => {
        const bd = fare.breakdown || {};
        const lines = bd.lines || [];
        const kmAll = Math.round(bd.totalKmBothWays || distanceKm || 0);
        const kmChip = kmAll > 0 ? fillText(t(isOneWay ? "hm_km" : "cv_kmBoth"), { km: kmAll }) : "";
        const durChip = durationText(bd.tripHours || hours, t);
        const chips = [tripKind, kmChip, durChip].filter(Boolean);
        const close = () => setShowFareBreakdown(false);
        return (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-[rgba(15,23,42,0.48)]" onClick={close}>
            <div role="dialog" aria-modal="true" aria-label={t("cv_priceDetails")}
              className="w-full max-w-6xl bg-white rounded-t-[22px] shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="px-4 pt-3 pb-2.5 border-b border-[#EEF2F7] shrink-0">
                <div className="w-[40px] h-[4px] rounded-full bg-[#E2E8F0] mx-auto mb-2.5" />
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-extrabold text-[#0F172A]">{t("cv_priceDetails")}</p>
                    <p className="mt-0.5 text-[10px] font-bold text-[#5E8700] leading-snug">{t("cv_setByPartner")}</p>
                  </div>
                  <button type="button" onClick={close} aria-label={t("cv_close")} className={RING_BTN}>
                    <Icon name="x" size={16} stroke="#475569" strokeWidth={2.2} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pt-3 pb-3 space-y-2.5" style={{ WebkitOverflowScrolling: "touch" }}>
                <div className="rounded-2xl bg-[#F1F7E3] border border-[#D6E6B0] px-3 py-2.5">
                  <p className="flex items-center gap-1.5 min-w-0 text-[12px] font-extrabold text-[#0F172A]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#7BAE00] shrink-0" />
                    <span className="truncate">{fromShort}</span>
                    <span className="text-[#94A3B8] shrink-0">→</span>
                    <Icon name="flag" size={13} stroke="#DC3545" strokeWidth={2.2} className="shrink-0" />
                    <span className="truncate">{toShort}</span>
                  </p>
                  {whenFrom && <p className="mt-1 text-[10px] text-[#3F5E00]">{whenFrom}{whenTo ? ` → ${whenTo}` : ""}</p>}
                  {chips.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {chips.map((c) => (
                        <span key={c} className="rounded-full bg-white border border-[#D6E6B0] px-2 py-[3px] text-[9.5px] font-extrabold text-[#3F5E00]">{c}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl bg-[#F8FAFC] border border-[#E5E7EB] px-3 py-2.5">
                  {lines.length > 0 && (
                    <div className="space-y-1.5 pb-2 mb-2 border-b border-[#E5E7EB]">
                      {lines.map((line, i) => (
                        <p key={i} className="flex justify-between gap-3 text-[11px] text-[#334155]">
                          <span className="min-w-0 leading-snug">{fareLineText(line.label, t)}</span>
                          <b className="shrink-0 font-bold text-[#0F172A]">{rupees(line.value)}</b>
                        </p>
                      ))}
                    </div>
                  )}
                  {saving > 0 && (
                    <p className="flex justify-between gap-3 text-[11px] text-[#64748B]">
                      <span>{t("cv_usualFare")}</span><span className="line-through">{rupees(usualFare)}</span>
                    </p>
                  )}
                  <p className="mt-1 flex items-center justify-between gap-3">
                    <span className="text-[12px] font-extrabold text-[#5E8700]">{t("cv_youPay")}</span>
                    <span className="text-[16px] font-extrabold text-[#5E8700]">{rupees(priceNow)}</span>
                  </p>
                </div>

                <div className="rounded-2xl border border-[#E5E7EB] px-3 py-2.5">
                  <p className="text-[11px] font-extrabold text-[#0F172A]">{t("cv_notIncl")}</p>
                  <ul className="mt-1 space-y-0.5 text-[10px] text-[#334155] leading-snug list-disc pl-4">
                    <li>{t("cv_notIncl1")}</li>
                    <li>{t("cv_notIncl2")}</li>
                  </ul>
                  {/* The calculator works from this partner's own rates, which only a car
                      opened from the list carries - a shared link has the price alone. */}
                  {!isColdLoad && (
                    <button type="button"
                      onClick={() => { setCalcHours(Math.round(bd.tripHours || hours)); setCalcKm(Math.round(bd.totalKmBothWays || distanceKm)); setCalcResult(null); close(); setShowPricingCalc(true); }}
                      className="mt-1.5 min-h-[40px] -mb-1.5 inline-flex items-center gap-0.5 text-[11px] font-extrabold text-[#5E8700] active:opacity-70">
                      {t("cv_calcLink")}<Icon name="chevronRight" size={14} strokeWidth={2.6} />
                    </button>
                  )}
                </div>
              </div>

              {!isUnavailable && (
                <div className="px-4 pt-2.5 pb-[max(14px,env(safe-area-inset-bottom))] border-t border-[#EEF2F7] shrink-0">
                  <button type="button" onClick={() => { close(); handleCallDriver(); }} disabled={loadingCall} aria-label={callAria}
                    className={`w-full h-[52px] text-[13px] ${GREEN_BTN}`}>
                    <Icon name="phone" size={17} stroke="#FFFFFF" fill="#FFFFFF" strokeWidth={1.4} />{t("hm_call")}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Price calculator - more km or hours on this partner's rates ── */}
      {showPricingCalc && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-[rgba(15,23,42,0.55)]" onClick={() => setShowPricingCalc(false)}>
          <div role="dialog" aria-modal="true" aria-label={t("cv_calcTitle")}
            className="w-full max-w-6xl bg-white rounded-t-[22px] shadow-2xl px-4 pt-3 pb-[max(18px,env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
            <div className="w-[40px] h-[4px] rounded-full bg-[#E2E8F0] mx-auto mb-2.5" />
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-extrabold text-[#0F172A]">{t("cv_calcTitle")}</p>
                <p className="mt-0.5 text-[10px] text-[#64748B]">{t("cv_calcSub")}</p>
              </div>
              <button type="button" onClick={() => setShowPricingCalc(false)} aria-label={t("cv_close")} className={RING_BTN}>
                <Icon name="x" size={16} stroke="#475569" strokeWidth={2.2} />
              </button>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2.5">
              <div>
                <p className="mb-1 text-[10px] font-bold text-[#64748B]">{t("cv_calcHours")}</p>
                <input type="number" inputMode="numeric" min="1" step="1" value={calcHours} onChange={(e) => setCalcHours(e.target.value)} aria-label={t("cv_calcHours")}
                  className="w-full h-[46px] px-3 rounded-xl border border-[#E5E7EB] text-[12px] font-semibold text-[#0F172A] outline-none focus:border-[#7BAE00]" />
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold text-[#64748B]">{t("cv_calcKm")}</p>
                <input type="number" inputMode="numeric" min="1" step="1" value={calcKm} onChange={(e) => setCalcKm(e.target.value)} aria-label={t("cv_calcKm")}
                  className="w-full h-[46px] px-3 rounded-xl border border-[#E5E7EB] text-[12px] font-semibold text-[#0F172A] outline-none focus:border-[#7BAE00]" />
              </div>
            </div>
            <button type="button"
              onClick={() => {
                if (calcHours > 0 && calcKm > 0) setCalcResult(calculateFare(car, calcHours, calcKm, tripType, cityPricing));
              }}
              className={`mt-3 w-full h-[48px] text-[12px] ${GREEN_BTN}`}>
              {t("cv_calcBtn")}
            </button>

            {calcResult && (
              <div className="mt-3 rounded-2xl bg-[#F8FAFC] border border-[#E5E7EB] px-3 py-2.5">
                <div className="space-y-1.5">
                  {calcResult.breakdown?.lines?.map((line, i) => (
                    <p key={i} className="flex justify-between gap-3 text-[11px] text-[#334155]">
                      <span className="min-w-0 leading-snug">{fareLineText(line.label, t)}</span>
                      <b className="shrink-0 font-bold text-[#0F172A]">{rupees(line.value)}</b>
                    </p>
                  ))}
                </div>
                <p className="mt-2 pt-2 border-t border-[#E5E7EB] flex items-center justify-between gap-3">
                  <span className="text-[12px] font-extrabold text-[#0F172A]">{t("cv_calcTotal")}</span>
                  <span className="text-[18px] font-extrabold text-[#5E8700]">{rupees(calcResult.final)}</span>
                </p>
                <p className="mt-2 rounded-md bg-[#F1F5F9] py-1 text-center text-[9.5px] text-[#64748B]">{t("cv_calcNote")}</p>
              </div>
            )}
          </div>
        </div>
      )}
      {instantOpen && car && (
        <InstantBookingSheet initialSeats={seatClassOf(car)} onClose={() => setInstantOpen(false)}
          onLogin={() => navigate("/login", { state: { redirectTo: location.pathname + location.search + location.hash, payload: { car, tripType, distanceKm, hours, fromLocation, toLocation, cityPricing, stops: state?.stops, callAfterLogin: true } } })}
          trip={{ city: car.city, from: fromLocation?.value || fromLocation?.name || fromLocation || "", to: toLocation?.value || toLocation?.name || toLocation || "",
            pickupAt: pickupDt, returnAt: dropDt, tripType: ["ROUND_TRIP", "OUTSTATION", "ROUND"].includes(tripType) ? "ROUND" : "ONE_WAY" }} />
      )}
    </div>
    )
  );
}
